import { Link } from 'react-router';
import { Shield, ArrowLeft, Lock, Eye, Database, Globe } from 'lucide-react';
import { motion } from 'motion/react';
import { SEO, SEO_CONFIGS } from '../components/SEO';

export function PrivacyPolicy() {
  return (
    <>
      <SEO {...SEO_CONFIGS.privacy} />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Header */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
                <p className="text-slate-600 mt-1">Last updated: March 9, 2026</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto px-6 py-12"
        >
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 md:p-12 space-y-8 prose prose-slate max-w-none">
            
            {/* Introduction */}
            <section>
              <p className="text-lg text-slate-700 leading-relaxed">
                CRRSoft, Inc. ("Company," "we," "us," or "our") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use HelioROI ("Service").
              </p>
              <p className="text-slate-700 leading-relaxed mt-4">
                By using the Service, you consent to the data practices described in this Privacy Policy. If you do not agree, please discontinue use of the Service.
              </p>
            </section>

            {/* 1. Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Database className="w-6 h-6 text-blue-600" />
                1. Information We Collect
              </h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">1.1 Information You Provide</h3>
              <p className="text-slate-700 leading-relaxed">
                We collect information that you voluntarily provide when using the Service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Account Information:</strong> Email address, password (encrypted), name, and billing information (company name, address, tax ID);</li>
                <li><strong>Project Data:</strong> Solar project parameters (location coordinates, system capacity, equipment specifications, financial assumptions);</li>
                <li><strong>Payment Information:</strong> Processed by Stripe (we do not store credit card numbers);</li>
                <li><strong>Communications:</strong> Support requests, feedback, and correspondence.</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">1.2 Automatically Collected Information</h3>
              <p className="text-slate-700 leading-relaxed">
                We automatically collect certain technical information when you use the Service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Log Data:</strong> IP address, browser type, operating system, access times, pages viewed;</li>
                <li><strong>Device Information:</strong> Device type, unique device identifiers;</li>
                <li><strong>Usage Data:</strong> Features accessed, analyses performed, credits consumed;</li>
                <li><strong>Cookies:</strong> Session tokens, authentication cookies (see Section 7).</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">1.3 Third-Party Data Sources</h3>
              <p className="text-slate-700 leading-relaxed">
                We retrieve solar irradiation and weather data from third-party APIs:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>NASA POWER API:</strong> Global solar irradiance and meteorological data;</li>
                <li><strong>Open-Meteo API:</strong> Weather forecasts and historical climate data.</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-2">
                Location coordinates you provide are sent to these services solely for data retrieval. We do not control these third parties' privacy practices.
              </p>
            </section>

            {/* 2. How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Eye className="w-6 h-6 text-purple-600" />
                2. How We Use Your Information
              </h2>
              <p className="text-slate-700 leading-relaxed">
                We use collected information for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Provide the Service:</strong> Process analyses, store projects, manage credits;</li>
                <li><strong>Account Management:</strong> Authenticate users, maintain account security, send verification emails;</li>
                <li><strong>Payment Processing:</strong> Process purchases through Stripe, issue invoices;</li>
                <li><strong>Improve the Service:</strong> Analyze usage patterns, fix bugs, develop new features;</li>
                <li><strong>Communications:</strong> Send transactional emails (password resets, purchase confirmations), respond to support requests;</li>
                <li><strong>Legal Compliance:</strong> Enforce Terms of Service, comply with legal obligations, prevent fraud;</li>
                <li><strong>Marketing (Opt-In Only):</strong> Send promotional emails (you may opt out at any time).</li>
              </ul>
            </section>

            {/* 3. Legal Basis for Processing (GDPR) */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">3. Legal Basis for Processing (GDPR Compliance)</h2>
              <p className="text-slate-700 leading-relaxed">
                For users in the European Economic Area (EEA), we process personal data under the following legal bases:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Contractual Necessity:</strong> To fulfill our obligations under the Terms of Service;</li>
                <li><strong>Consent:</strong> For marketing communications (you may withdraw consent at any time);</li>
                <li><strong>Legitimate Interests:</strong> To improve the Service, prevent fraud, and ensure security;</li>
                <li><strong>Legal Obligation:</strong> To comply with tax laws, data protection regulations, and court orders.</li>
              </ul>
            </section>

            {/* 4. Data Sharing and Disclosure */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Globe className="w-6 h-6 text-orange-600" />
                4. Data Sharing and Disclosure
              </h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">4.1 We Do Not Sell Your Data</h3>
              <p className="text-slate-700 leading-relaxed">
                <strong>We never sell, rent, or trade your personal information to third parties for their marketing purposes.</strong>
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">4.2 Service Providers</h3>
              <p className="text-slate-700 leading-relaxed">
                We share data with trusted third-party service providers who assist in operating the Service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Supabase:</strong> Database hosting, authentication, backend infrastructure (subprocessor);</li>
                <li><strong>Stripe:</strong> Payment processing (see Stripe's Privacy Policy);</li>
                <li><strong>NASA POWER & Open-Meteo:</strong> Solar and weather data retrieval (location coordinates only);</li>
                <li><strong>Email Service (Resend):</strong> Transactional email delivery.</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-2">
                These providers are contractually obligated to protect your data and use it only for specified purposes.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">4.3 Legal Requirements</h3>
              <p className="text-slate-700 leading-relaxed">
                We may disclose your information if required by law or in good faith belief that such action is necessary to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Comply with legal obligations, court orders, or subpoenas;</li>
                <li>Protect our rights, property, or safety, or that of our users;</li>
                <li>Investigate fraud, security breaches, or Terms violations.</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">4.4 Business Transfers</h3>
              <p className="text-slate-700 leading-relaxed">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity. You will be notified via email of any such change in ownership.
              </p>
            </section>

            {/* 5. Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Lock className="w-6 h-6 text-red-600" />
                5. Data Security
              </h2>
              <p className="text-slate-700 leading-relaxed">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Encryption:</strong> All data transmitted over HTTPS/TLS; passwords are hashed using bcrypt;</li>
                <li><strong>Access Controls:</strong> Role-based access restrictions; admin-only features protected by JWT authentication;</li>
                <li><strong>Infrastructure Security:</strong> Hosted on Supabase with enterprise-grade security compliance (SOC 2, ISO 27001);</li>
                <li><strong>Regular Audits:</strong> Periodic security reviews and vulnerability assessments.</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                <strong className="text-red-600">However, no method of transmission or storage is 100% secure.</strong> While we strive to protect your data, we cannot guarantee absolute security. You are responsible for maintaining the confidentiality of your account credentials.
              </p>
            </section>

            {/* 6. Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">6. Data Retention</h2>
              <p className="text-slate-700 leading-relaxed">
                We retain your data for as long as necessary to provide the Service and comply with legal obligations:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Account Data:</strong> Retained until account deletion (you may request deletion at any time);</li>
                <li><strong>Project Data:</strong> Retained for the duration of your account;</li>
                <li><strong>Transaction Records:</strong> Retained for 7 years for tax compliance (required by U.S. law);</li>
                <li><strong>Log Data:</strong> Retained for 90 days for security and debugging purposes.</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                Upon account deletion, we will permanently delete your personal data within 30 days, except where retention is required by law.
              </p>
            </section>

            {/* 7. Cookies and Tracking */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">7. Cookies and Tracking Technologies</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">7.1 Essential Cookies</h3>
              <p className="text-slate-700 leading-relaxed">
                We use cookies necessary for the Service to function:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Authentication Cookie:</strong> Stores your session token (JWT) to keep you logged in;</li>
                <li><strong>Security Cookie:</strong> Prevents cross-site request forgery (CSRF) attacks.</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">7.2 Analytics (Future)</h3>
              <p className="text-slate-700 leading-relaxed">
                Currently, we do not use analytics cookies. If we implement analytics in the future, we will update this policy and provide an opt-out mechanism.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">7.3 Managing Cookies</h3>
              <p className="text-slate-700 leading-relaxed">
                You can disable cookies in your browser settings, but this may prevent you from accessing certain features of the Service.
              </p>
            </section>

            {/* 8. Your Privacy Rights */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">8. Your Privacy Rights</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">8.1 Access and Portability</h3>
              <p className="text-slate-700 leading-relaxed">
                You have the right to request a copy of your personal data in a structured, machine-readable format.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">8.2 Correction</h3>
              <p className="text-slate-700 leading-relaxed">
                You may update your account information at any time through the Profile Settings page.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">8.3 Deletion</h3>
              <p className="text-slate-700 leading-relaxed">
                You may request deletion of your account and personal data by contacting us at info@helioroi.com. We will process your request within 30 days, subject to legal retention requirements.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">8.4 Opt-Out of Marketing</h3>
              <p className="text-slate-700 leading-relaxed">
                You may opt out of promotional emails by clicking "Unsubscribe" in any marketing email or by contacting us.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">8.5 GDPR Rights (EEA Users)</h3>
              <p className="text-slate-700 leading-relaxed">
                If you are located in the EEA, you have additional rights under GDPR:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Right to restriction of processing;</li>
                <li>Right to object to processing;</li>
                <li>Right to lodge a complaint with your local data protection authority.</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">8.6 California Privacy Rights (CCPA)</h3>
              <p className="text-slate-700 leading-relaxed">
                California residents have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Know what personal information we collect and how it is used;</li>
                <li>Request deletion of personal information;</li>
                <li>Opt out of the sale of personal information (we do not sell data);</li>
                <li>Non-discrimination for exercising privacy rights.</li>
              </ul>
            </section>

            {/* 9. International Data Transfers */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">9. International Data Transfers</h2>
              <p className="text-slate-700 leading-relaxed">
                Your data may be transferred to and processed in the United States or other countries where our service providers operate. We ensure that adequate safeguards are in place (e.g., Standard Contractual Clauses) to protect your data in compliance with GDPR and other applicable laws.
              </p>
            </section>

            {/* 10. Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">10. Children's Privacy</h2>
              <p className="text-slate-700 leading-relaxed">
                The Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected data from a minor, please contact us immediately, and we will delete it.
              </p>
            </section>

            {/* 11. Changes to This Policy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">11. Changes to This Privacy Policy</h2>
              <p className="text-slate-700 leading-relaxed">
                We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. Material changes will be communicated via email or a notice on the Service. The "Last Updated" date at the top indicates the most recent revision.
              </p>
              <p className="text-slate-700 leading-relaxed mt-4">
                Continued use of the Service after changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            {/* Contact */}
            <section className="border-t border-slate-200 pt-8">
              <h2 className="text-2xl font-bold text-slate-900">12. Contact Information</h2>
              <p className="text-slate-700 leading-relaxed">
                For questions about this Privacy Policy or to exercise your privacy rights, please contact us:
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mt-4">
                <p className="text-slate-900 font-semibold">CRRSoft, Inc.</p>
                <p className="text-slate-700">Data Protection Officer</p>
                <p className="text-slate-700">7901 4TH ST N STE 300</p>
                <p className="text-slate-700">ST. PETERSBURG, FL 33702</p>
                <p className="text-slate-700">United States</p>
                <p className="text-slate-700 mt-2">Phone: <a href="tel:+17273902400" className="text-blue-600 hover:underline">+1 (727) 390-2400</a></p>
                <p className="text-slate-700">Email: <a href="mailto:info@helioroi.com" className="text-blue-600 hover:underline">info@helioroi.com</a></p>
                <p className="text-slate-700 mt-2">Service: HelioROI</p>
                <p className="text-slate-700">Website: <a href="https://helioroi.com" className="text-blue-600 hover:underline">https://helioroi.com</a></p>
              </div>
            </section>

            {/* Footer Notice */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-8">
              <p className="text-sm text-green-900">
                <strong>Your Privacy Matters:</strong> We are committed to transparency and protecting your personal information. If you have any concerns about how we handle your data, please don't hesitate to contact us at info@helioroi.com.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}